import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Loader2, ThumbsUp, MessageCircle, Share2, ExternalLink,
  ClipboardList, ShieldAlert, Inbox, SlidersHorizontal, Check, X,
  AlertOctagon, AlertTriangle, Info, CheckCircle2, Newspaper, Zap,
  Flame, ThumbsUp as ThumbsUpMini, Building2,
} from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import { ShareToChatButton } from "@/components/ShareToChat";

const CRIT = {
  critical: { icon: AlertOctagon, color: "#E24B4A", label: "Critique" },
  high: { icon: AlertTriangle, color: "#EF9F27", label: "Élevée" },
  medium: { icon: Info, color: "#378ADD", label: "Moyenne" },
  low: { icon: CheckCircle2, color: "#1D9E75", label: "Faible" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} j`;
}

function cleanText(s) {
  if (!s) return "";
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value.replace(/\s+/g, " ").trim();
}

export default function Feed() {
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [showTopics, setShowTopics] = useState(false);
  const sentinelRef = useRef(null);

  const loadPrefs = useCallback(async () => {
    const r = await api.get("/workspace/feed/preferences");
    setPrefs(r.data);
    if (!r.data.configured) setShowTopics(true);
  }, []);

  const loadFeed = useCallback(async (reset = false, cur = null) => {
    if (!reset) setLoadingMore(true);
    try {
      const r = await api.get("/workspace/feed", { params: { limit: 20, ...(cur ? { cursor: cur } : {}) } });
      setItems((prev) => (reset || !prev ? r.data.items : [...prev, ...r.data.items]));
      setCursor(r.data.next_cursor);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setLoadingMore(false); }
  }, []);

  useEffect(() => { loadPrefs().catch(() => {}); loadFeed(true); }, [loadPrefs, loadFeed]);

  useEffect(() => {
    if (!sentinelRef.current || !cursor) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore) loadFeed(false, cursor);
    }, { rootMargin: "400px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [cursor, loadingMore, loadFeed]);

  const nTopics = prefs ? (prefs.sectors.length + prefs.categories.length) : 0;

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="feed-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="noam-overline">Votre veille, vos sujets</div>
          <h1 className="noam-display text-2xl font-semibold flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#378ADD]" /> Feed
          </h1>
        </div>
        <button onClick={() => setShowTopics(true)} className="btn-ghost text-xs" data-testid="feed-topics-btn">
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Mes sujets {nTopics > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#378ADD]/20 text-[#378ADD] text-[10px] font-semibold">{nTopics}</span>}
        </button>
      </div>

      <CerclePulse />

      {items === null && (
        <div className="noam-card p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div>
      )}

      {items && items.length === 0 && (
        <div className="noam-card p-10 text-center text-zinc-400 text-sm" data-testid="feed-empty">
          Aucune actualité ne correspond à vos sujets suivis.
          <button onClick={() => setShowTopics(true)} className="block mx-auto mt-3 btn-primary text-xs">Élargir mes sujets</button>
        </div>
      )}

      {items && items.map((sig) => (
        <FeedCard key={sig.id} sig={sig} />
      ))}

      <div ref={sentinelRef} />
      {loadingMore && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></div>}
      {items && items.length > 0 && !cursor && (
        <div className="py-6 text-center text-xs text-zinc-600">Vous êtes à jour ✓</div>
      )}

      {showTopics && prefs && (
        <TopicsModal
          prefs={prefs}
          onClose={() => setShowTopics(false)}
          onSaved={async () => { setShowTopics(false); await loadPrefs(); await loadFeed(true); }}
        />
      )}
    </div>
  );
}

function CerclePulse() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/workspace/feed/cercle-pulse")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  if (items === null || items.length === 0) return null;
  return (
    <div className="noam-card p-4 border border-[#EF9F27]/20 bg-gradient-to-br from-[#EF9F27]/[0.06] to-transparent" data-testid="feed-cercle-pulse">
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-4 h-4 text-[#EF9F27]" />
        <span className="text-sm font-semibold text-white">Vu par le Cercle</span>
        <span className="text-[10px] text-zinc-500">Ce que les autres organisations regardent en ce moment — anonymisé</span>
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <a
            key={it.id}
            href={it.url || undefined}
            target={it.url ? "_blank" : undefined}
            rel="noreferrer"
            className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-white/5 transition-colors group"
            data-testid={`cercle-pulse-item-${it.id}`}
          >
            <span className="text-lg font-bold text-[#EF9F27]/40 w-5 text-center flex-shrink-0">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-200 truncate group-hover:text-white">{cleanText(it.title)}</div>
              <div className="text-[10px] text-zinc-500 truncate">{it.source_name} · il y a {timeAgo(it.published_at)}</div>
            </div>
            <div className="flex items-center gap-2.5 text-[10px] text-zinc-500 flex-shrink-0">
              {it.likes > 0 && <span className="flex items-center gap-1"><ThumbsUpMini className="w-3 h-3 text-[#378ADD]" />{it.likes}</span>}
              {it.comments > 0 && <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-[#1D9E75]" />{it.comments}</span>}
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#EF9F27]/10 border border-[#EF9F27]/25 text-[#EF9F27]">
                <Building2 className="w-3 h-3" />{it.org_count} org{it.org_count > 1 ? "s" : ""}
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function FeedCard({ sig }) {
  const [liked, setLiked] = useState(sig.liked_by_me);
  const [likes, setLikes] = useState(sig.likes_count);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsCount, setCommentsCount] = useState(sig.comments_count);
  const [chainOpen, setChainOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const crit = CRIT[sig.criticality];

  const toggleLike = async () => {
    try {
      const r = await api.post(`/workspace/feed/${sig.id}/like`);
      setLiked(r.data.liked); setLikes(r.data.likes_count);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const share = async () => {
    const text = `${sig.title}\n${sig.url || ""}`;
    try {
      if (navigator.share) await navigator.share({ title: sig.title, url: sig.url });
      else { await navigator.clipboard.writeText(text); notify.success("Lien copié dans le presse-papiers."); }
    } catch { /* annulé */ }
  };

  const act = (kind) => async () => {
    setBusy(kind);
    try {
      if (kind === "lead") {
        const r = await api.post(`/workspace/feed/${sig.id}/to-lead`);
        notify[r.data.already_existed ? "info" : "success"](r.data.already_existed ? "Un lead existe déjà pour cette actualité." : "Lead créé — visible dans Leads.");
      } else if (kind === "plan") {
        const r = await api.post(`/workspace/trend/signals/${sig.id}/to-plan`);
        notify[r.data.already_existed ? "info" : "success"](r.data.already_existed ? "Un plan existe déjà pour cette actualité." : `Plan d'action créé${r.data.ai_generated ? " avec 3 actions IA" : ""} — visible dans Plans d'action.`);
      } else if (kind === "risque") {
        const r = await api.post(`/workspace/trend/signals/${sig.id}/convert-to-risque`);
        notify.success(`Risque créé : ${r.data.code || ""}`);
      }
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(null); }
  };

  return (
    <article className="noam-card p-5 space-y-3" data-testid={`feed-card-${sig.id}`}>
      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
        <span className="font-medium text-zinc-400 truncate max-w-[45%]">{sig.source_name}</span>
        <span>·</span>
        <span>il y a {timeAgo(sig.published_at)}</span>
        {crit && (
          <span className="ml-auto flex items-center gap-1" style={{ color: crit.color }}>
            <crit.icon className="w-3 h-3" /> {crit.label}
          </span>
        )}
      </div>
      <h2 className="text-[15px] font-semibold text-white leading-snug">
        {sig.url ? (
          <a href={sig.url} target="_blank" rel="noreferrer" className="hover:text-[#378ADD] transition-colors">
            {cleanText(sig.title)} <ExternalLink className="w-3 h-3 inline opacity-50" />
          </a>
        ) : cleanText(sig.title)}
      </h2>
      {sig.summary && <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3">{cleanText(sig.summary)}</p>}
      {(sig.sectors || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {sig.sectors.slice(0, 4).map((s) => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-400">{s}</span>
          ))}
        </div>
      )}
      {(sig.chain_impacts || []).length > 0 && (
        <button
          onClick={() => setChainOpen((v) => !v)}
          className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border transition-colors ${chainOpen ? "border-[#EF9F27]/60 bg-[#EF9F27]/15 text-[#EF9F27]" : "border-[#EF9F27]/30 bg-[#EF9F27]/5 text-[#EF9F27]/90 hover:bg-[#EF9F27]/15"}`}
          data-testid={`feed-chain-btn-${sig.id}`}
        >
          <Zap className="w-3.5 h-3.5" />
          Choc en chaîne : {sig.chain_impacts.map((c) => c.name).join(", ")}
        </button>
      )}
      {chainOpen && <ChainImpact sigId={sig.id} />}
      <div className="flex items-center gap-1 pt-1 border-t border-white/5 flex-wrap">
        <button onClick={toggleLike}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md transition-colors ${liked ? "text-[#378ADD] bg-[#378ADD]/10" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
          data-testid={`feed-like-${sig.id}`}>
          <ThumbsUp className="w-3.5 h-3.5" /> {likes > 0 && likes}
        </button>
        <button onClick={() => setCommentsOpen((v) => !v)}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md transition-colors ${commentsOpen ? "text-[#378ADD] bg-[#378ADD]/10" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
          data-testid={`feed-comments-btn-${sig.id}`}>
          <MessageCircle className="w-3.5 h-3.5" /> {commentsCount > 0 && commentsCount}
        </button>
        <button onClick={share} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/5 transition-colors" data-testid={`feed-share-${sig.id}`}>
          <Share2 className="w-3.5 h-3.5" /> Partager
        </button>
        <ShareToChatButton attachment={{ kind: "signal", id: sig.id }} title={cleanText(sig.title)} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md text-zinc-400 hover:text-[#1D9E75] hover:bg-white/5 transition-colors" />
        <span className="flex-1" />
        <button onClick={act("lead")} disabled={busy === "lead"} className="text-[11px] px-2 py-1.5 rounded-md text-zinc-400 hover:text-[#378ADD] hover:bg-white/5 disabled:opacity-50" data-testid={`feed-to-lead-${sig.id}`}>
          {busy === "lead" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Inbox className="w-3 h-3 inline" />} Lead
        </button>
        <button onClick={act("plan")} disabled={busy === "plan"} className="text-[11px] px-2 py-1.5 rounded-md text-zinc-400 hover:text-[#1D9E75] hover:bg-white/5 disabled:opacity-50" data-testid={`feed-to-plan-${sig.id}`}>
          {busy === "plan" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <ClipboardList className="w-3 h-3 inline" />} Plan
        </button>
        <button onClick={act("risque")} disabled={busy === "risque"} className="text-[11px] px-2 py-1.5 rounded-md text-zinc-400 hover:text-[#E24B4A] hover:bg-white/5 disabled:opacity-50" data-testid={`feed-to-risque-${sig.id}`}>
          {busy === "risque" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <ShieldAlert className="w-3 h-3 inline" />} Risque
        </button>
      </div>
      {commentsOpen && <Comments sigId={sig.id} onCount={setCommentsCount} />}
    </article>
  );
}

function ChainImpact({ sigId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/workspace/feed/${sigId}/chain-impact`)
      .then((r) => setData(r.data.touched))
      .catch((e) => notify.error(fmtError(e)));
  }, [sigId]);

  if (data === null) return <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />;
  return (
    <div className="rounded-md border border-[#EF9F27]/25 bg-[#EF9F27]/5 p-3 space-y-3" data-testid={`feed-chain-impact-${sigId}`}>
      {data.map((t) => (
        <div key={t.entity.entity_id || t.entity.id} className="space-y-1.5">
          <div className="text-xs font-semibold text-[#EF9F27]">{t.entity.name} — exposition dans votre chaîne de valeur</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
            <div>
              <div className="text-zinc-500 uppercase tracking-wider text-[9px] mb-1">Amont exposé ({t.upstream.length})</div>
              {t.upstream.length === 0 && <div className="text-zinc-600">—</div>}
              {t.upstream.map((u, i) => (
                <div key={i} className="text-zinc-300">← {u.name} <span className="text-zinc-500">({u.flow_type.replace("_", " & ")})</span></div>
              ))}
            </div>
            <div>
              <div className="text-zinc-500 uppercase tracking-wider text-[9px] mb-1">Aval exposé ({t.downstream.length})</div>
              {t.downstream.length === 0 && <div className="text-zinc-600">—</div>}
              {t.downstream.map((u, i) => (
                <div key={i} className="text-zinc-300">→ {u.name} <span className="text-zinc-500">({u.flow_type.replace("_", " & ")})</span></div>
              ))}
            </div>
          </div>
        </div>
      ))}
      <a href="/app/annuaire" className="inline-block text-[10px] text-[#EF9F27] underline underline-offset-2">Ouvrir la vue SPLC →</a>
    </div>
  );
}

function Comments({ sigId, onCount }) {
  const [rows, setRows] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/workspace/feed/${sigId}/comments`);
    setRows(r.data.items);
    onCount(r.data.items.length);
  }, [sigId, onCount]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/workspace/feed/${sigId}/comments`, { text });
      setText("");
      await load();
    } catch (er) { notify.error(fmtError(er)); }
    finally { setSending(false); }
  };

  return (
    <div className="pt-2 space-y-2" data-testid={`feed-comments-${sigId}`}>
      {rows === null && <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />}
      {rows && rows.map((c) => (
        <div key={c.id} className="flex gap-2 text-sm">
          <div className="w-6 h-6 rounded-full bg-[#378ADD]/20 text-[#378ADD] text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
            {(c.user_name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <span className="text-xs font-medium text-zinc-300">{c.user_name}</span>
            <span className="text-[10px] text-zinc-600 ml-1.5">il y a {timeAgo(c.created_at)}</span>
            <p className="text-sm text-zinc-400 break-words">{c.text}</p>
          </div>
        </div>
      ))}
      <form onSubmit={send} className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Votre commentaire (visible par votre équipe)…"
          className="noam-input flex-1 text-sm"
          data-testid={`feed-comment-input-${sigId}`}
        />
        <button type="submit" disabled={sending || !text.trim()} className="btn-primary text-xs disabled:opacity-50" data-testid={`feed-comment-send-${sigId}`}>
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Publier"}
        </button>
      </form>
    </div>
  );
}

function TopicsModal({ prefs, onClose, onSaved }) {
  const [sectors, setSectors] = useState(new Set(prefs.sectors));
  const [categories, setCategories] = useState(new Set(prefs.categories));
  const [saving, setSaving] = useState(false);

  const toggle = (setter) => (v) => setter((s) => {
    const n = new Set(s);
    if (n.has(v)) n.delete(v); else n.add(v);
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/workspace/feed/preferences", { sectors: [...sectors], categories: [...categories] });
      notify.success("Sujets enregistrés — votre feed et votre Daily News sont personnalisés.");
      onSaved();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="feed-topics-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">Personnalisation</div>
            <div className="noam-display text-base font-semibold mt-0.5">Mes sujets suivis</div>
            <p className="text-xs text-zinc-500 mt-1">Votre feed et votre email Daily News n'afficheront que ces sujets. Aucune sélection = tout voir.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-xs text-zinc-300 font-medium mb-2">Secteurs</div>
            <div className="flex flex-wrap gap-1.5">
              {prefs.available.sectors.map((s) => (
                <button key={s} onClick={() => toggle(setSectors)(s)}
                  className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${sectors.has(s) ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-[#378ADD] font-medium" : "border-white/10 text-zinc-400 hover:text-white"}`}
                  data-testid={`feed-topic-sector-${s}`}>
                  {sectors.has(s) && <Check className="w-3 h-3 inline mr-1" />}{s}
                </button>
              ))}
            </div>
          </div>
          {prefs.available.categories.length > 0 && (
            <div>
              <div className="text-xs text-zinc-300 font-medium mb-2">Thèmes</div>
              <div className="flex flex-wrap gap-1.5">
                {prefs.available.categories.map((c) => (
                  <button key={c} onClick={() => toggle(setCategories)(c)}
                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${categories.has(c) ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-[#378ADD] font-medium" : "border-white/10 text-zinc-400 hover:text-white"}`}
                    data-testid={`feed-topic-category-${c}`}>
                    {categories.has(c) && <Check className="w-3 h-3 inline mr-1" />}{c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-white/5">
          <button onClick={onClose} className="btn-ghost text-xs">Annuler</button>
          <button onClick={save} disabled={saving} className="btn-primary text-xs disabled:opacity-50" data-testid="feed-topics-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
